import * as L from 'leaflet'

import { BlockOrGetData, customPost } from '../../../utils/http'
import { inputS3, tileApiKey, tileAppUrl } from '../../../utils/config'
import { ImageData } from '../../../utils/types'


const pollSlideInfo = async (payload: any) => {
    const maxAttempts = 24;
    let attempts = 0;

    while (attempts < maxAttempts) {
        const res = await customPost(`${tileAppUrl}/tile/slide`, payload, {
            headers: {
                "Content-Type": "application/json",
                "X-API-Key": tileApiKey
            }
        });
        if (res.status === "Done") return true
        // Wait 5 second before trying again
        await new Promise(resolve => setTimeout(resolve, 5000))
        attempts++
    }
    return false
}
 
const getMapProps = async (imageData: ImageData) => {
    let xmlMaxZoom = 0, url = '', slide_width = 0, slide_height = 0, customTileData = null
    try {
        let xml_data
        try {
            xml_data = await BlockOrGetData(`${imageData.base_raw_download_url}/tilemapresource.xml`, false)
        } catch {
            xml_data = await BlockOrGetData(`${imageData.base_raw_download_url}.${imageData.file_type}/tilemapresource.xml`, false)
        }
        
        const parser = new DOMParser()
        const xmlDoc = parser.parseFromString(xml_data.data, 'text/xml')
        slide_width = Math.abs(parseInt(xmlDoc.getElementsByTagName('BoundingBox')[0].attributes[2].value))
        slide_height = Math.abs(parseInt(xmlDoc.getElementsByTagName('BoundingBox')[0].attributes[1].value))
        xmlMaxZoom = xmlDoc.getElementsByTagName("MaxZoom")[0] ? Number(xmlDoc.getElementsByTagName("MaxZoom")[0].attributes[0].value) : 8
        // There are 4 types of directory structure due a change in the tiling process and added file extensions.
        try {
            await BlockOrGetData(`${imageData.base_raw_download_url}/0/0/0.png`, false)
            url = xmlDoc.getElementsByTagName("MaxZoom")[0]
                ? `${imageData.base_raw_download_url}/{z}/{y}/{x}.png`
                : `${imageData.base_raw_download_url}/{z}/{x}/{y}.png`
        } catch {
            await BlockOrGetData(`${imageData.base_raw_download_url}.${imageData.file_type}/0/0/0.png`, false)
            url = xmlDoc.getElementsByTagName("MaxZoom")[0]
                ? `${imageData.base_raw_download_url}.${imageData.file_type}/{z}/{y}/{x}.png`
                : `${imageData.base_raw_download_url}.${imageData.file_type}/{z}/{x}/{y}.png`
        }
    } catch (err) {
        console.warn('Tiles not found in S3, trying API for tiles')
        const payload = {
            bucket: imageData.input_s3 || inputS3,
            key: imageData.input_s3 ? imageData.name : `${imageData.image_unique_id}.${imageData.file_type || 'svs'}`,
            slide_id: imageData.image_unique_id,
            z: 0,
            x: 0,
            y: 0
        }
        const slideExist = await pollSlideInfo(payload);
        if (slideExist) {
            const slideInfo = await customPost(`${tileAppUrl}/tile/slide-info`, payload, {
                headers: {
                    "Content-Type": "application/json",
                    "X-API-Key": tileApiKey
                }
            });
            xmlMaxZoom = slideInfo.max_zoom
            slide_width = slideInfo.resolution[0]
            slide_height = slideInfo.resolution[1]
            // Custom POST request data for fetching tiles
            customTileData = {
                payload: payload,
                config: {
                    responseType: 'blob',
                    headers: {
                        "Content-Type": "application/json",
                        'Accept': 'image/avif,image/webp,image/apng,image/svg+xml,image/*,*/*;q=0.8',
                        "X-API-Key": tileApiKey
                    }
                }
            }
        } else throw new Error("Slide processing timed out.")
    }

    // Calculate what the true 1:1 max zoom level must be based on slide dimensions
    // Protect against empty/overzoomed folder declarations
    const theoreticalMaxZoom = Math.ceil(Math.log2(Math.max(slide_width, slide_height) / 256))
    const safeMaxZoom = Math.min(xmlMaxZoom, theoreticalMaxZoom)

    return { maxZoom: safeMaxZoom, url, slide_width, slide_height, customTileData }
}

// Helper function to add CSS for hiding broken images
const addTileErrorStyles = () => {
  // Check if styles already added
  if (document.getElementById('leaflet-tile-error-styles')) return
  
  const style = document.createElement('style')
  style.id = 'leaflet-tile-error-styles'
  style.textContent = `
    .leaflet-tile-error {
      opacity: 0 !important;
      visibility: hidden !important;
    }
  `
  document.head.appendChild(style)
}

interface TileCoords {
  x: number
  y: number
  z: number
  [key: string]: any
}

interface CustomTilePayload {
  x: number
  y: number
  z: number
  [key: string]: any
}

interface CustomTileConfig {
  [key: string]: any
}

interface CustomTileData {
  payload: CustomTilePayload
  config: CustomTileConfig
}

interface CustomTileLayerOptions extends L.LayerOptions {
  maxZoom?: number
  minNativeZoom?: number
  noWrap?: boolean
  updateWhenIdle?: boolean
  updateWhenZooming?: boolean
  tileSize?: number
  updateInterval?: number
  fallbackUrl?: string
  keepBuffer?: number
}

interface FallbackTile extends HTMLImageElement {
  _originalCoords?: TileCoords;
  _originalSrc?: string;
}

interface CacheStats {
    keys: string[]
    tiles: number
    maxTiles: number
    memoryUsed: string
    memoryLimit: string
}

class TileCacheManager {
    private _cache: Map<string, string>
    private _cacheKeys: string[]
    private _maxSize: number
    private _maxMemoryBytes: number
    private _currentMemoryBytes: number

    constructor(maxSize: number = 350, maxMemoryMB: number = 140) {
        this._cache = new Map()
        this._cacheKeys = []
        this._maxSize = maxSize
        this._maxMemoryBytes = maxMemoryMB * 1048576
        this._currentMemoryBytes = 0
    }

    private _estimateSize(value: string): number {
        if (value.startsWith('data:')) {
            // Base64 data URL - storing the ENTIRE image in cache
            const base64Data = value.split(',')[1] || ''
            return base64Data.length * 2    // 2 bytes per character in UTF-16
        } else {
            // Regular URL - we're only storing the URL string
            // The actual image is cached by the browser separately
            return value.length * 2 + 200
        }
    }

    initialize(maxSize: number = 350, maxMemoryMB: number = 140): void {
        this._maxSize = maxSize
        this._cache = new Map()
        this._cacheKeys = []
        this._maxMemoryBytes = maxMemoryMB * 1048576
        this._currentMemoryBytes = 0
    }
    get(key: string): string | null {
        if (!this._cache.has(key)) return null

        // Update LRU: move to end (most recently used)
        const index = this._cacheKeys.indexOf(key)
        if (index > -1) {
            this._cacheKeys.splice(index, 1)
            this._cacheKeys.push(key)
        }

        return this._cache.get(key) || null
    }
    set(key: string, value: string): void {
        const valueSize = this._estimateSize(value)

        // If already in cache, update LRU order
        if (this._cache.has(key)) {
            const index = this._cacheKeys.indexOf(key)
            if (index > -1) {
                this._cacheKeys.splice(index, 1)
            }
            this._cacheKeys.push(key)
            return
        }

        // Evict tiles if we exceed memory limit
        while (
            (this._cache.size >= this._maxSize ||
                this._currentMemoryBytes + valueSize > this._maxMemoryBytes) &&
            this._cacheKeys.length > 0
        ) {
            const oldestKey = this._cacheKeys.shift()
            if (oldestKey) {
                const oldValue = this._cache.get(oldestKey)
                if (oldValue) {
                    this._currentMemoryBytes -= this._estimateSize(oldValue)
                }
                this._cache.delete(oldestKey)
            }
        }

        // Add new tile to cache
        this._cache.set(key, value)
        this._cacheKeys.push(key)
        this._currentMemoryBytes += valueSize
    }
    clear(): void {
        this._cache.clear()
        this._cacheKeys = []
        this._currentMemoryBytes = 0
    }
    getStats(): CacheStats {
        return {
            keys: [...this._cacheKeys],
            tiles: this._cache.size,
            maxTiles: this._maxSize,
            memoryUsed: `${(this._currentMemoryBytes / 1048576).toFixed(2)}MB`,
            memoryLimit: `${(this._maxMemoryBytes / 1048576).toFixed(2)}MB`
        }
    }
}

// Default GET tile layer with caching and fallback
const cachedFallbackGetLayer = (url: string, maxZoom: number) => {
    const tileCache = new TileCacheManager()

    const CustomTileLayer = L.GridLayer.extend({
        options: {
            maxZoom: maxZoom,
            minNativeZoom: 0,
            tileSize: 256,
            noWrap: true,
            updateWhenIdle: true,
            updateWhenZooming: false,
            reuseTiles: true,
            fallbackUrl: url,
            keepBuffer: 4
        } as CustomTileLayerOptions,

        initialize: function (options?: CustomTileLayerOptions) {
            L.setOptions(this, options)
            L.GridLayer.prototype.initialize.call(this, options)
            addTileErrorStyles()
        },
        createTile: function (coords: TileCoords, done: any): HTMLImageElement {
            const tile = document.createElement('img') as FallbackTile

            // FORCE CORS: so other libs don't show CORS error when using cached tiles
            tile.crossOrigin = 'anonymous'

            const tileKey = this._getTileKey(coords)

            // Store original coordinates for fallback
            tile._originalCoords = coords
            tile._originalSrc = this._getTileUrl(coords)  

            // Check cache first
            const cachedUrl = tileCache.get(tileKey)
            if (cachedUrl) {
                tile.src = cachedUrl
                tile.onload = () => done(null, tile)
                tile.onerror = () => this._tileOnError(tile, done) // Cached tile failed, load fresh
                return tile
            }

            // If not in cache, try to load from URL
            this._loadTile(tile, coords, done)
            return tile
        },
        _loadTile: function (tile: HTMLImageElement, coords: TileCoords, done: any): void {
            const tileUrl = this._getTileUrl(coords)
            const tileKey = this._getTileKey(coords)
            tile.onload = () => {
                tileCache.set(tileKey, tileUrl)
                tile.classList.remove('leaflet-tile-error')
                done(null, tile)
            }
            tile.onerror = () => this._tileOnError(tile, done)
            tile.src = tileUrl
        },
        _createCurrentCoords: function (originalCoords: TileCoords) {
            const currentCoords = { ...originalCoords }
            currentCoords.fallback = true
            return currentCoords
        },
        _tileOnError: function (tile: any, done: any): void {
            const layer = this
            const originalCoords = tile._originalCoords

            // Safety check: ensure originalCoords exists
            if (!originalCoords || originalCoords.z === undefined) {
                tile.classList.add('leaflet-tile-error')
                done(null, tile)
                return
            }

            const currentCoords = {...(tile._currentCoords || layer._createCurrentCoords(originalCoords))}
            tile._currentCoords = currentCoords
            const fallbackZoom = tile._fallbackZoom = tile._fallbackZoom === undefined ? originalCoords.z - 1 : tile._fallbackZoom - 1
            const scale = tile._fallbackScale = (tile._fallbackScale || 1) * 2
            const tileSize = layer.getTileSize()
            const style = tile.style

            // If no lower zoom tiles are available, mark as error
            if (fallbackZoom < layer.options.minNativeZoom) {
                tile.classList.add('leaflet-tile-error')
                done(null, tile)
                return
            }

            // Modify coordinates for replacement tile
            currentCoords.z = fallbackZoom
            currentCoords.x = Math.floor(currentCoords.x / 2)
            currentCoords.y = Math.floor(currentCoords.y / 2)

            // Generate new URL
            const newUrl = layer._getTileUrlFallback(currentCoords)

            // Scale the replacement tile
            style.width = (tileSize.x * scale) + 'px'
            style.height = (tileSize.y * scale) + 'px'

            // Compute margins to adjust position
            const top = (originalCoords.y - currentCoords.y * scale) * tileSize.y
            style.marginTop = (-top) + 'px'
            const left = (originalCoords.x - currentCoords.x * scale) * tileSize.x
            style.marginLeft = (-left) + 'px'

            // Crop (clip) image
            style.clip = `rect(${top}px ${left + tileSize.x}px ${top + tileSize.y}px ${left}px)`

            // Fire event for debugging
            layer.fire('tilefallback', {
                tile: tile,
                url: tile._originalSrc,
                urlMissing: tile.src,
                urlFallback: newUrl
            })

            // Try loading fallback tile
            tile.onload = () => {
                const fallbackKey = layer._getTileKey(currentCoords)
                tileCache.set(fallbackKey, newUrl)
                tile.classList.remove('leaflet-tile-error')
                done(null, tile)
            }
            tile.onerror = () => this._tileOnError(tile, done)
            tile.src = newUrl
        },
        _getTileUrl: function (coords: TileCoords): string {
            // Replace template variables in URL
            return this.options.fallbackUrl
                .replace('{z}', String(coords.z))
                .replace('{x}', String(coords.x))
                .replace('{y}', String(coords.y))
                .replace('{s}', this._getSubdomain(coords))
        },
        _getTileUrlFallback: function (coords: TileCoords): string {
            // Use the same URL template but with fallback coordinates
            return this._getTileUrl(coords)
        },
        _getSubdomain: function (coords: TileCoords): string {
            // Simple subdomain selection (a, b, c)
            const subdomains = ['a', 'b', 'c']
            const index = Math.abs(coords.x + coords.y) % subdomains.length
            return subdomains[index]
        },
        _getTileKey: function (coords: TileCoords): string {
            return `${coords.z}_${coords.x}_${coords.y}`
        }
    })

    const tileLayer = new CustomTileLayer()

    return { tileLayer, tileCache }
}

// Custom POST tile layer with caching and fallback
const cachedFallbackPostLayer = (customTileData: CustomTileData, maxZoom: number) => {
    const tileCache = new Map<string, Blob>()

    const CustomTileLayer = L.GridLayer.extend({
        options: {
            maxZoom: maxZoom,
            noWrap: true,
            updateWhenIdle: true,
            updateWhenZooming: false,
            reuseTiles: true,
            updateInterval: 300,
            keepBuffer: 2
        } as CustomTileLayerOptions,
        initialize: function (options?: CustomTileLayerOptions) {
            L.setOptions(this, options)
            L.GridLayer.prototype.initialize.call(this, options)
            this._debouncedUpdate = this._debounce(this._update, 300)
        },
        onAdd: function (map: any) {
            L.GridLayer.prototype.onAdd.call(this, map)
            this._map = map
            map.on('moveend', this._debouncedUpdate, this)
            this._debouncedUpdate()
        },
        onRemove: function (map: any) {
            map.off('moveend', this._debouncedUpdate, this)
            L.GridLayer.prototype.onRemove.call(this, map)
        },
        createTile: function (coords: TileCoords, done: any): HTMLImageElement {
            const tile = document.createElement('img')

            // FORCE CORS: so other libs don't show CORS error when using cached tiles
            tile.crossOrigin = 'anonymous'

            const tileKey = this._getTileKey(coords)
            
            // Check cache first
            const cachedBlob = tileCache.get(tileKey)
            if (cachedBlob) {
                const url = URL.createObjectURL(cachedBlob)
                tile.src = url
                tile.onload = () => done(null, tile)
                tile.onerror = () => done('Cached tile failed', tile)
                return tile
            }

            // If not in cache, request it via POST
            this._requestTile(coords)
                .then((blob: Blob) => {
                    if (!(blob instanceof Blob)) throw new Error("Response is not a blob");
                    tileCache.set(tileKey, blob)
                    const url = URL.createObjectURL(blob)
                    tile.onload = () => done(null, tile)
                    tile.onerror = () => {
                        URL.revokeObjectURL(url);
                        done('Tile load error', tile)
                    }
                    tile.src = url
                })
                .catch((err: Error) => {
                    done(new Error('Tile failed to load'))
                })

            return tile
        },
        _requestTile: async function (coords: TileCoords): Promise<Blob> {
            if (!coords || typeof coords.x === 'undefined' ||
                typeof coords.y === 'undefined' || typeof coords.z === 'undefined') {
                throw new Error('Invalid tile coordinates')
            }

            customTileData.payload.x = Number(coords.x)
            customTileData.payload.y = Number(coords.y)
            customTileData.payload.z = Number(coords.z)

            return await customPost(`${tileAppUrl}/tile/get-tile`, customTileData.payload, customTileData.config)
        },
        _debounce: function (fn: Function, delay: number): (...args: any[]) => void {
            let timer: ReturnType<typeof setTimeout>
            return function (this: any, ...args: any[]) {
                const context = this
                clearTimeout(timer)
                timer = setTimeout(() => {
                    if (this._map) fn.apply(context, args)
                }, delay)
            }
        },
        _update: function () {
            if (!this._map) return

            const bounds = this._map.getPixelBounds()
            const zoom = this._map.getZoom()

            if (isNaN(zoom) || !bounds || !bounds.min || !bounds.max) return

            L.GridLayer.prototype._update.call(this)
        },
        _getTileKey: function (coords: TileCoords): string {
            return `${coords.z}_${coords.x}_${coords.y}`
        }
    })

    const tileLayer = new CustomTileLayer()

    return tileLayer
}

export {
    getMapProps,
    cachedFallbackGetLayer,
    cachedFallbackPostLayer
}